/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarService } from '@services/requests/housien-services/CalendarService';
import {
  expandEvents,
  type ExpandedEvent,
} from '@features/agenda/utils/expand-recurrence';
import { agendaKeys } from './agenda-keys';

/**
 * Builds a stable cache key for a date range and optional calendar.
 * @param start - Range start date.
 * @param end - Range end date.
 * @param calendarId - Optional calendar id scope.
 * @returns The composed range key string.
 */
const rangeKey = (start: Date, end: Date, calendarId?: string): string =>
  `${start.toISOString()}|${end.toISOString()}|${calendarId ?? 'all'}`;

/**
 * Fetch events for an agency and expand recurrences over the visible range.
 *
 * @param agencyId The agency id (query disabled when empty).
 * @param rangeStart Visible window start.
 * @param rangeEnd Visible window end.
 * @param calendarId Optional calendar filter.
 */
export const useCalendarEvents = (
  agencyId: string,
  rangeStart: Date,
  rangeEnd: Date,
  calendarId?: string
) => {
  const query = useQuery({
    queryKey: agendaKeys.events(
      agencyId,
      rangeKey(rangeStart, rangeEnd, calendarId)
    ),
    queryFn: async () => {
      const response = await CalendarService.listEvents(agencyId, calendarId);
      return response.data ?? [];
    },
    enabled: Boolean(agencyId),
  });

  const events: ExpandedEvent[] = useMemo(
    () => expandEvents(query.data ?? [], rangeStart, rangeEnd),
    [query.data, rangeStart, rangeEnd]
  );

  return { ...query, events };
};
